import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { checkStatus } from '../utilities/fetch';
import classNames from 'classnames';
import { curatedListPath, curatedListAdminPath } from '../utilities/api_relative_paths';
import { createQueryString, deleteCachedElement } from '../utilities/utilities';
import { GENERIC_ERROR_MESSAGE } from '../components/react/alert_popup/constants';
import { Typeahead, Highlighter } from 'react-bootstrap-typeahead-for-bs3';
import 'react-bootstrap-typeahead-for-bs3/css/Typeahead.css';
import { RECRUITER_SEARCH_PATH } from '../utilities/devex_paths';
import styles from './add_to_curated_list_tool.module.scss';

const AddToCuratedListTool = ({
  consultantId,
  consultantInCCLs,
  setConsultantInCCLs,
  setAlertData
}) => {
  const [targetCCL, setTargetCCL] = useState([]);
  const [autocompleteEntries, setAutocompleteEntries] = useState([]);
  const [currentNumberOfCCLs, setCurrentNumberOfCCLs] = useState(consultantInCCLs.length);
  const [disabledActions, setDisabledActions] = useState(false);

  // Update the candidate cache after adding or removing a CCL
  const updateCandidateCache = (successMessage) => {
    deleteCachedElement(consultantId, 'Consultant')
      .then(() => {
        setAlertData({ type: 'success', message: successMessage });
      })
      .catch(() => {
        setAlertData({ type: 'error', message: GENERIC_ERROR_MESSAGE });
      });
  };

  /**
   * Perform request to fetch all current user's CCLs. Will then be used as autocomplete entries.
   *
   * @returns {undefined}
   */
  const fetchAutocompleteEntries = () => {
    const params = {
      page: {
        size: 100,
      },
    };

    fetch(`${curatedListPath}${createQueryString(params)}`)
      .then(checkStatus)
      .then((res) => res.json())
      .then((data) => {
        const filteredCCLs = data.data.map((ccl) => {
          return {
            ...ccl,
            selected: !!consultantInCCLs.find((c) => c.id === ccl.id),
          };
        });
        setAutocompleteEntries(filteredCCLs);
      });
  };

  useEffect(fetchAutocompleteEntries, []);

  /**
   * Saves user in an existing Curated Candidate List. Note that user needs to select valid
   * target CCL in the Autocomplete input (either existing or new) to save the consultant.
   * Also, note that, to add a consultant to an existing CCL, we just need the name of the
   * existing CCL.
   *
   * @returns {undefined}
   */
  const addToCCL = () => {
    setDisabledActions(true);
    const params = {
      consultant_id: consultantId,
    };
    const URL = `${curatedListAdminPath}/${targetCCL[0].id}/candidates`;

    fetch(`${URL}${createQueryString(params)}`, { method: 'POST' })
      .then(checkStatus)
      .then((response) => response.json())
      .then(() => {
        const filteredCCLs = autocompleteEntries.map((ccl) => {
          return {
            ...ccl,
            selected: ccl.id === targetCCL[0].id ? true : ccl.selected,
          };
        });
        setConsultantInCCLs(consultantInCCLs.concat(targetCCL));
        setTargetCCL([]);
        setAutocompleteEntries(filteredCCLs);
        setCurrentNumberOfCCLs(currentNumberOfCCLs + 1);
        updateCandidateCache(`Candidate successfully added to curated list ${targetCCL[0].name}`);
      })
      .catch(() => {
        setAlertData({ type: 'error', message: GENERIC_ERROR_MESSAGE });
      })
      .finally(() => setDisabledActions(false));
  };

  /**
   * Remove from Curated Candidate List
   *
   * @param {object} CCLToRemove - Tag as object
   *
   * @returns {undefined}
   */
  const removeFromCCL = (CCLToRemove) => {
    setDisabledActions(true);
    const URL = `${curatedListAdminPath}/${CCLToRemove.id}/candidates/${consultantId}`;

    fetch(URL, { method: 'DELETE' })
      .then(checkStatus)
      .then(() => {
        const newConsultantCCLs = consultantInCCLs.filter((ccl) => {
          return ccl.id !== CCLToRemove.id;
        });
        const newAutocompleteEntries = autocompleteEntries.map((ccl) => {
          return { ...ccl, selected: CCLToRemove.id === ccl.id ? false : ccl.selected };
        });
        setAutocompleteEntries(newAutocompleteEntries);
        setConsultantInCCLs(newConsultantCCLs);
        setCurrentNumberOfCCLs(newConsultantCCLs.length);
        updateCandidateCache(
          `Candidate successfully removed from curated list ${CCLToRemove.name}`
        );
      })
      .catch(() => {
        setAlertData({ type: 'error', message: GENERIC_ERROR_MESSAGE });
      })
      .finally(() => setDisabledActions(false));
  };

  const filterBy = (ccl, data) => {
    const isValidName = ccl.name.toLowerCase().includes(data.text.toLowerCase());
    return ccl.selected === false && isValidName;
  };

  return (
    <div className={styles.addToCCLTool}>
      <div className={`${styles.selectCCL} margin-top-large`}>
        <div className={`input-group ${styles.addToCCLInput}`}>
          <Typeahead
            id='add-to-ccl-typeahead'
            labelKey='name'
            onChange={(selected) => setTargetCCL(selected)}
            options={autocompleteEntries}
            filterBy={filterBy}
            placeholder='Add to Curated Candidate List'
            selected={targetCCL}
            disabled={disabledActions}
            renderMenuItemChildren={(option, { text }) => (
              <div>
                <Highlighter search={text}>{option.name}</Highlighter>
                {option.workflow_state === 'draft' ? ' (Draft)' : ''}
              </div>
            )}
          />
          <span className={`input-group-btn ${styles.addToCCLButton}`}>
            <button
              onClick={addToCCL}
              className='btn btn-line-primary h-padding-large'
              disabled={disabledActions || !targetCCL.length}
            >
              Add
            </button>
          </span>
        </div>
      </div>
      <div className='ccls-list margin-top-small'>
        <div className='ccl-list'>
          {consultantInCCLs.map((ccl) => {
            return (
              <div
                className={classNames(styles.cclItem, {
                  [styles.disabled]: disabledActions,
                  [styles.draftCCL]: ccl.workflow_state === 'draft',
                  [styles.publishedCCL]: ccl.workflow_state === 'published',
                })}
                key={ccl.id}
              >
                <a
                  className={`tagging-text link-blue-orange ${styles.cclBadgeText}`}
                  target='_blank'
                  href={`${RECRUITER_SEARCH_PATH}?filter[curated_candidate_list_ids][]=${ccl.id}`}
                  aria-disabled={disabledActions}
                  rel='noreferrer'
                >
                  {ccl.name}
                </a>
                <a
                  className={styles.removeCCL}
                  onClick={() => removeFromCCL(ccl)}
                  aria-disabled={disabledActions}
                ></a>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

AddToCuratedListTool.propTypes = {
  consultantId: PropTypes.number,
  setCurrentNumberOfCCLs: PropTypes.func,
  consultantInCCLs: PropTypes.array,
  setConsultantInCCLs: PropTypes.func,
  setAlertData: PropTypes.func.isRequired,
};

export default AddToCuratedListTool;
